import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { DealerGroupsState } from '../../interfaces/dealer-group.interface';
import { catchError, map, scan, shareReplay, take, tap } from 'rxjs/operators';
import { DealerGroupAction, reducer, Selector, SetState, ToggleAll, ToggleGroup, ToggleMember } from './reducer';
import { dtoToDealerGroups } from './util';
import { DealerGroupMemberDto, OrganizationService } from 'remarketing-angular-library';
import { LeadSellerPreferencesService } from '../lead-seller-preferences/lead-seller-preferences.service';

@Injectable({
  providedIn: 'root'
})
export class LeadSellerGroupService {
  private static initialState: DealerGroupsState = {
    memberEntities: {},
    memberUuids: [],
    groupEntities: {},
    groupUuids: []
  };

  private searchTerm = new BehaviorSubject<string>('');

  private actions = new BehaviorSubject<DealerGroupAction>(new SetState(LeadSellerGroupService.initialState));

  /**
   * Every time a new response is received, the response is mapped to the expected state.
   * Every time an action is received, it is applied to the current state. If a new response is received the state is reset.
   */
  readonly state$: Observable<DealerGroupsState> = this.actions.asObservable().pipe(
    scan(reducer, undefined),
    tap((state: DealerGroupsState) => this.currentState.next(state)),
    shareReplay(1)
  );

  readonly searchTerm$: Observable<string> = this.searchTerm.asObservable();

  readonly leadSellerResponse$ = new BehaviorSubject<boolean>(false);

  private currentState = new BehaviorSubject<DealerGroupsState>(undefined);

  constructor(
    private readonly organizationService: OrganizationService,
    private leadSellerPreferencesService: LeadSellerPreferencesService
  ) {}

  snapShot() {
    return this.currentState.getValue();
  }

  loadLeadSellerGroups(leadSellerExcludedDealers) {
    this.organizationService
    .getLeadSellerGroupMembers()
    .pipe(
      catchError(err => {
        console.error('Failed to get lead seller group members', err);
        return of([]);
      }),
      tap(res => {
        const leadSellerStoreValue = dtoToDealerGroups(res, leadSellerExcludedDealers);
        if (sessionStorage.getItem('leadSeller') === null) {

          this.setState(leadSellerStoreValue);
          /**
           * Added the excluded dealers into the session storage during the initial preferences call.
           * This allows default preferences to be reused after logging in and refreshing without caching the state.
           */
          sessionStorage.setItem('leadSeller', JSON.stringify(leadSellerStoreValue));
        } else {
          this.setState(JSON.parse(sessionStorage.getItem('leadSeller')));
        }

        this.leadSellerPreferencesService.setState(leadSellerStoreValue);
        if (!this.leadSellerResponse$.getValue()) {
          this.leadSellerResponse$.next(true);
        }
      })
    )
    .subscribe();
  }

  getAllLeadSellerDealerGroups(): Observable<DealerGroupMemberDto[]>  {
    return this.organizationService.getLeadSellerGroupMembers().pipe(
      catchError(err => {
        console.error('Failed to get lead seller dealers', err);
        return of([]);
      }));
  }

  toggleGroup(uuid: string) {
    this.actions.next(new ToggleGroup(uuid));
  }

  toggleAll() {
    this.actions.next(new ToggleAll());
  }

  toggleMember(uuid: string) {
    this.actions.next(new ToggleMember(uuid));
  }

  setState(state: DealerGroupsState) {
    this.actions.next(new SetState(state));
  }

  select<T>(selectFn: Selector<T>): Observable<T> {
    return this.state$.pipe(map(selectFn));
  }

  search(searchTerm: string) {
    this.searchTerm.next(searchTerm);
  }

  cacheState() {
    sessionStorage.setItem('leadSeller', JSON.stringify(this.snapShot()));
  }

  getLeadSellerResponse$() {
    return this.leadSellerResponse$;
  }

}
